/**
 * "Como fazer?" — Central de Ajuda do site público (/ajuda e /ajuda/:slug).
 * O conteúdo vive em lib/help.ts (partilhado com o Manual do Utilizador).
 */
import { AlertTriangle, ArrowLeft, ArrowRight, Boxes, FileText, Landmark, Lightbulb, LifeBuoy, Receipt, Rocket, Search, ShieldCheck, Undo2 } from 'lucide-react';
import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { HELP, HELP_CATEGORIES, type HelpCategoryId, searchHelp } from '../lib/help';
import { PortalPageHero } from './portal';

const CAT_ICON: Record<HelpCategoryId, ReactNode> = {
  comecar: <Rocket />, vender: <FileText />, corrigir: <Undo2 />, receber: <Receipt />, stock: <Boxes />, fiscal: <Landmark />, conta: <ShieldCheck />,
};

/** **negrito** → <b>; *itálico* → <i>. Nada mais. */
function Rich({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return <>{parts.map((p, i) => p.startsWith('**') ? <b key={i} className="font-semibold text-[#111]">{p.slice(2, -2)}</b>
    : p.startsWith('*') && p.length > 2 ? <i key={i}>{p.slice(1, -1)}</i> : <Fragment key={i}>{p}</Fragment>)}</>;
}

export function PortalHelp() {
  const [q, setQ] = useState('');
  const results = useMemo(() => (q.trim() ? searchHelp(q) : null), [q]);
  return (
    <>
      <PortalPageHero eyebrow="Como fazer?" title="Guias curtos, passo a passo.">
        Escreva o que quer fazer — "anular factura", "recibo", "ficheiro da AT" — ou escolha um tema.
      </PortalPageHero>
      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
        <div className="relative -mt-7 mb-12 max-w-2xl">
          <Search className="pointer-events-none absolute left-5 top-1/2 size-5 -translate-y-1/2 text-[#5d5d63]" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ex.: como anular uma factura" aria-label="Pesquisar na ajuda"
            className="h-14 w-full rounded-2xl bg-white pl-14 pr-5 text-[16px] shadow-[0_12px_30px_-12px_rgb(17_17_17/.25)] ring-1 ring-black/10 outline-none transition focus:ring-2 focus:ring-brand-700/40" />
        </div>

        {results ? (
          <div>
            <p className="mb-4 text-sm text-[#5d5d63]">{results.length ? `${results.length} ${results.length === 1 ? 'guia encontrado' : 'guias encontrados'}` : 'Nada encontrado com essas palavras.'}</p>
            {results.length ? <ArticleList items={results} /> : <NoResult />}
          </div>
        ) : (
          <div className="grid gap-x-10 gap-y-12 md:grid-cols-2">
            {HELP_CATEGORIES.map((c) => (
              <div key={c.id} id={c.id} className="scroll-mt-24">
                <div className="mb-4 flex items-center gap-3">
                  <span className="grid size-10 place-items-center rounded-xl bg-brand-700 text-white [&>svg]:size-[18px]">{CAT_ICON[c.id]}</span>
                  <div><h2 className="font-display text-lg font-bold leading-tight">{c.title}</h2><p className="text-sm text-[#5d5d63]">{c.blurb}</p></div>
                </div>
                <ArticleList items={HELP.filter((a) => a.cat === c.id)} compact />
              </div>
            ))}
          </div>
        )}
        <div className="mt-16"><NoResult /></div>
      </section>
    </>
  );
}

function ArticleList({ items, compact }: { items: typeof HELP; compact?: boolean }) {
  return (
    <ul className="divide-y divide-black/[.06] overflow-hidden rounded-2xl bg-white ring-1 ring-black/[.07]">
      {items.map((a) => (
        <li key={a.slug}>
          <Link to={`/ajuda/${a.slug}`} className="group flex items-center gap-4 px-5 py-4 transition hover:bg-brand-50/50">
            <div className="min-w-0 flex-1">
              <div className="font-medium group-hover:text-brand-700">{a.title}</div>
              {!compact && <div className="mt-0.5 line-clamp-1 text-sm text-[#5d5d63]">{a.intro}</div>}
            </div>
            <ArrowRight className="size-4 shrink-0 text-[#5d5d63] transition group-hover:translate-x-0.5 group-hover:text-brand-700" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function NoResult() {
  return (
    <div className="flex flex-col items-start gap-4 rounded-2xl bg-[#111] p-7 text-white sm:flex-row sm:items-center">
      <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-brand-700"><LifeBuoy className="size-6" /></span>
      <div className="flex-1"><div className="font-display text-lg font-semibold">Não encontrou o que procurava?</div><p className="text-white/65">Diga-nos o que está a tentar fazer. Ajudamos — e o guia que faltava passa a existir.</p></div>
      <Link to="/suporte" className="inline-flex h-11 items-center gap-2 rounded-xl bg-white px-5 font-semibold text-brand-700 hover:bg-brand-50">Falar com o suporte</Link>
    </div>
  );
}

export function PortalHelpArticle() {
  const { slug } = useParams();
  const a = HELP.find((x) => x.slug === slug);
  if (!a) return <Navigate to="/ajuda" replace />;
  const cat = HELP_CATEGORIES.find((c) => c.id === a.cat)!;
  const siblings = HELP.filter((x) => x.cat === a.cat && x.slug !== a.slug);
  return (
    <section className="mx-auto grid max-w-6xl gap-12 px-4 py-12 sm:px-6 lg:grid-cols-[1fr_300px]">
      <article className="min-w-0">
        <nav className="mb-6 flex items-center gap-2 text-sm text-[#5d5d63]">
          <Link to="/ajuda" className="inline-flex items-center gap-1 hover:text-brand-700"><ArrowLeft className="size-4" />Como fazer?</Link>
          <span>/</span><Link to={`/ajuda#${cat.id}`} className="hover:text-brand-700">{cat.title}</Link>
        </nav>
        <h1 className="font-display text-3xl font-bold leading-tight sm:text-4xl">{a.title}</h1>
        <p className="mt-3 text-lg leading-relaxed text-[#3A3A3B]">{a.intro}</p>

        <ol className="mt-10 space-y-0">
          {a.steps.map((s, i) => (
            <li key={i} className="relative flex gap-5 pb-8 last:pb-0">
              {i < a.steps.length - 1 && <span aria-hidden className="absolute left-[19px] top-11 bottom-1 w-px bg-black/10" />}
              <span className="relative grid size-10 shrink-0 place-items-center rounded-xl bg-white font-display font-bold text-brand-700 ring-1 ring-brand-700/25">{i + 1}</span>
              <p className="pt-2 text-[16.5px] leading-relaxed text-[#3A3A3B]"><Rich text={s} /></p>
            </li>
          ))}
        </ol>

        {a.tip && (
          <div className="mt-10 flex gap-3 rounded-2xl bg-emerald-50 p-5 text-[15px] leading-relaxed text-emerald-950 ring-1 ring-emerald-600/15">
            <Lightbulb className="mt-0.5 size-5 shrink-0 text-emerald-600" /><p><b>Dica.</b> <Rich text={a.tip} /></p>
          </div>
        )}
        {a.warn && (
          <div className="mt-4 flex gap-3 rounded-2xl bg-amber-50 p-5 text-[15px] leading-relaxed text-amber-950 ring-1 ring-amber-600/20">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600" /><p><b>Atenção.</b> <Rich text={a.warn} /></p>
          </div>
        )}
      </article>

      <aside className="space-y-6 lg:pt-12">
        {siblings.length > 0 && (
          <div>
            <h2 className="mb-3 text-[13px] font-semibold uppercase tracking-[.14em] text-[#5d5d63]">Também em {cat.title}</h2>
            <ul className="space-y-1">{siblings.map((s) => <li key={s.slug}><Link to={`/ajuda/${s.slug}`} className="block rounded-lg px-3 py-2 text-[15px] hover:bg-black/[.04] hover:text-brand-700">{s.title}</Link></li>)}</ul>
          </div>
        )}
        <div className="rounded-2xl bg-[#f6f6f7] p-5">
          <div className="font-semibold">Ficou com dúvidas?</div>
          <p className="mt-1 text-sm text-[#5d5d63]">Mande-nos uma mensagem com o que estava a fazer.</p>
          <Link to="/suporte" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:underline">Falar com o suporte <ArrowRight className="size-4" /></Link>
        </div>
      </aside>
    </section>
  );
}
